import { useState, useEffect } from 'react';

/**
 * Hook that debounces a value by a specified delay
 * Useful for search inputs to avoid excessive API calls
 */
export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Hook for debounced search functionality
 * Returns both immediate and debounced values
 */
export const useDebouncedSearch = (initialValue: string = '', delay: number = 300) => {
  const [searchValue, setSearchValue] = useState(initialValue);
  const debouncedSearchValue = useDebounce(searchValue, delay);

  return {
    searchValue,
    debouncedSearchValue,
    setSearchValue,
  };
};
